import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Dialog, DialogContent, DialogActions, Button, CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { getPreviousQuarter } from "../../utils/dateUtils";
import { DEFAULT_JOB_FAMILIES, Employee } from "../../services/bonus/types";
import { Avatar } from "./../qba-theme/Avatar";
import { bonusApi } from "../../services/bonus/api";
import { prefetchOverrideEmployees, useOverrideEmployeePrefetch } from "../../hooks/useOverrideEmployeePrefetch";
import { overrideEmployeeIdb } from "../../services/cache/overrideEmployeeIdb";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VP_TITLES = new Set(['VP of Construction', 'Sr VP Construction']);
const APP_TYPE_TITLES: Record<string, string> = {
    CCM1: 'Customer Care Mgr I',
    CCM2: 'Customer Care Mgr II',
    CCM3: 'Customer Care Mgr III',
    CCM3Manager: 'Customer Care Mgr III',
    DCCM: 'Division Cust. Care Mgr.',
    CM3: 'C M III',
    PM: 'Project Manager',
    SPM: 'Sr Project Manager',
};

const ROLE_APPLICATION_TYPES: Record<string, { label: string; value: string }[]> = {
    DM: [{ label: "CM3", value: "CM3" }, { label: "PM", value: "PM" }, { label: "SPM", value: "SPM" }],
    "DM-Review": [{ label: "CM3", value: "CM3" }, { label: "PM", value: "PM" }, { label: "SPM", value: "SPM" }],
    DCCM: [{ label: "CCM1", value: "CCM1" }, { label: "CCM2", value: "CCM2" }, { label: "CCM3", value: "CCM3" }, { label: "CCM3 Manager", value: "CCM3Manager" }],
    "DCCM-Review": [{ label: "CCM1", value: "CCM1" }, { label: "CCM2", value: "CCM2" }, { label: "CCM3", value: "CCM3" }, { label: "CCM3 Manager", value: "CCM3Manager" }],
    DCCM_Review: [{ label: "CCM1", value: "CCM1" }, { label: "CCM2", value: "CCM2" }, { label: "CCM3", value: "CCM3" }, { label: "CCM3 Manager", value: "CCM3Manager" }],
    VP: [{ label: "DCCM", value: "DCCM" }],
    "VP-Review": [{ label: "DCCM", value: "DCCM" }],
};

const getRouteForApplicationType = (t: string): string =>
    ({ CM3: "/CM3-PM", PM: "/CM3-PM", SPM: "/SPM", CCM1: "/CCM", CCM2: "/CCM", CCM3: "/CCM", CCM3Manager: "/DCCM", DCCM: "/DCCM" } as Record<string, string>)[t] ?? "/CM3-PM";

const APPLICATION_TYPE_LABELS: Record<string, string> = {
    CCM1: "CCM1", CCM2: "CCM2", CCM3: "CCM3",
    CCM3Manager: "CCM3 Manager",
    DCCM: "DCCM", CM3: "CM3", PM: "PM", SPM: "SPM",
};

const getRoleTypeLabel = (role: string) => {
    if (['DM', 'DM-Review'].includes(role)) return 'DM';
    if (['DCCM', 'DCCM-Review', 'DCCM_Review'].includes(role)) return 'DCCM';
    if (['VP', 'VP-Review'].includes(role)) return 'VP';
    return role;
};

type AvatarRole = "dm" | "pm" | "vp" | "ccm" | "spm" | "ops";

const getRoleVariant = (role: string): AvatarRole => {
    if (['DM', 'DM-Review'].includes(role)) return 'dm';
    if (['DCCM', 'DCCM-Review', 'DCCM_Review'].includes(role)) return 'ccm';
    if (['VP', 'VP-Review'].includes(role)) return 'vp';
    return 'dm';
};

const getRecipientRoleVariant = (appType: string): AvatarRole => {
    if (['CCM1', 'CCM2', 'CCM3', 'CCM3Manager', 'DCCM'].includes(appType)) return 'ccm';
    if (appType === 'SPM') return 'spm';
    return 'pm';
};

const VP_ROLES = new Set(['VP', 'VP-Review']);

// MUI Paper sx — uses CSS variables so it responds to data-theme
const PAPER_SX = {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05)",
    mx: { xs: 2, sm: "auto" },
    background: "var(--card)",
    color: "var(--card-foreground)",
    border: "1px solid var(--border)",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BonusApplicationDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (bonusApplicationId: number) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const BonusApplicationDialog: React.FC<BonusApplicationDialogProps> = ({ open, onClose }) => {
    const navigate = useNavigate();

    const selectedRole = localStorage.getItem("selectedRole") ?? "";
    const loggedInUserEmail = (localStorage.getItem("loggedInUserEmail") ?? "").replace(/"/g, "");
    const loggedInUserName = (localStorage.getItem("loggedInUserFullName") ?? "").replace(/"/g, "");
    const defaultVpEmail = (localStorage.getItem("loggedInUserSupervisorEmail") ?? "").replace(/"/g, "");
    const defaultVpName = (localStorage.getItem("loggedInUserSupervisorName") ?? "").replace(/"/g, "");

    const prevQ = getPreviousQuarter();
    const year = prevQ.year.toString();
    const quarter = `Q${prevQ.quarter}`;

    const applicationTypeOptions = ROLE_APPLICATION_TYPES[selectedRole] ?? [];
    const isVpRole = VP_ROLES.has(selectedRole);
    const roleTypeLabel = getRoleTypeLabel(selectedRole);
    const roleVariant = getRoleVariant(selectedRole);

    const [applicationType, setApplicationType] = useState("");
    const [applicantSearch, setApplicantSearch] = useState("");
    const [selectedApplicant, setSelectedApplicant] = useState<Employee | null>(null);
    const [showApplicantDropdown, setShowApplicantDropdown] = useState(false);
    const [selectedVp, setSelectedVp] = useState<Employee | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [overrideHierarchy, setOverrideHierarchy] = useState(false);

    const applicantWrapRef = useRef<HTMLDivElement>(null);
    const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [overrideEmployees, setOverrideEmployees] = useState<Employee[]>([]);
    useOverrideEmployeePrefetch();

    useEffect(() => {
        if (!overrideHierarchy) return;
        setIsLoading(true);
        bonusApi.getOverrideEmployees({ jobFamily: DEFAULT_JOB_FAMILIES })
            .then(result => setOverrideEmployees(result.employees))
            .catch(() => setOverrideEmployees([]))
            .finally(() => setIsLoading(false));
    }, [overrideHierarchy]);

    useEffect(() => {
        if (!open) return;
        setIsLoading(true);
        bonusApi.getEmployees({ isExEmployee: false })
            .then(setAllEmployees)
            .catch(() => setAllEmployees([]))
            .finally(() => setIsLoading(false));
    }, [open]);

    const vpEmployees = useMemo(() => allEmployees.filter(e => VP_TITLES.has(e.JobTitle)), [allEmployees]);

    const applicantPool = useMemo(() => {
        if (overrideHierarchy) return overrideEmployees.filter(e => !VP_TITLES.has(e.JobTitle));
        const nonVps = allEmployees.filter(e => !VP_TITLES.has(e.JobTitle));
        if (!applicationType) return nonVps;
        const t = APP_TYPE_TITLES[applicationType];
        return t ? nonVps.filter(e => e.JobTitle === t && e.ApplicationType === applicationType) : nonVps;
    }, [allEmployees, applicationType, overrideHierarchy, overrideEmployees]);

    const filteredApplicationTypeOptions = useMemo(() => {
        if (!applicationTypeOptions.length) return [];
        if (overrideHierarchy) return applicationTypeOptions;
        return applicationTypeOptions.filter(opt =>
           applicantPool.some(emp => APP_TYPE_TITLES[opt.value] === emp.JobTitle && emp.ApplicationType === opt.value)
        );
    }, [applicationTypeOptions, applicantPool, overrideHierarchy]);

    const filteredApplicants = applicantSearch.trim()
        ? applicantPool.filter(e => {
            const q = applicantSearch.toLowerCase();
            return e.EmployeeName.toLowerCase().includes(q)
                || e.JobTitle.toLowerCase().includes(q)
                || e.EmployeeNumber.toLowerCase().includes(q);
        })
        : applicantPool;

    const hasNoEmployees = !isLoading && allEmployees.length === 0;
    const hasNoVp = !isLoading && !isVpRole && vpEmployees.length === 0 && allEmployees.length > 0;

    useEffect(() => {
        if (!vpEmployees.length) return;
        const match = vpEmployees.find(v => v.EmployeeEmail.toLowerCase() === defaultVpEmail.toLowerCase());
        setSelectedVp(match ?? vpEmployees[0] ?? null);
    }, [vpEmployees, defaultVpEmail]);

    useEffect(() => { setSelectedApplicant(null); setApplicantSearch(""); }, [applicationType]);

    // Reset applicant selection when override toggle changes
    useEffect(() => { setSelectedApplicant(null); setApplicantSearch(""); setShowApplicantDropdown(false); }, [overrideHierarchy]);

    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (applicantWrapRef.current && !applicantWrapRef.current.contains(e.target as Node))
                setShowApplicantDropdown(false);
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    useEffect(() => {
        if (overrideHierarchy) return;
        if (applicantPool.length === 1) {
            setSelectedApplicant(applicantPool[0]);
            setApplicantSearch(applicantPool[0].EmployeeName);
        }
    }, [applicantPool, overrideHierarchy]);

    useEffect(() => {
        if (filteredApplicationTypeOptions.length === 1) {
            const onlyType = filteredApplicationTypeOptions[0].value;
            if (onlyType !== applicationType) {
                setApplicationType(onlyType);
            }
        }
    }, [filteredApplicationTypeOptions, applicationType]);

    const handleSelectApplicant = (emp: Employee) => {
        setSelectedApplicant(emp); setApplicantSearch(emp.EmployeeName); setShowApplicantDropdown(false);
    };

    const resetForm = useCallback(() => {
        if (applicationTypeOptions.length !== 1) setApplicationType("");
        setApplicantSearch(""); setSelectedApplicant(null);
        setShowApplicantDropdown(false); setValidationError(null);
        setOverrideHierarchy(false);
        if (vpEmployees.length) {
            const match = vpEmployees.find(v => v.EmployeeEmail.toLowerCase() === defaultVpEmail.toLowerCase());
            setSelectedVp(match ?? vpEmployees[0] ?? null);
        } else setSelectedVp(null);
    }, [applicationTypeOptions.length, vpEmployees, defaultVpEmail]);

    const fullReset = () => {
        setApplicationType(""); setApplicantSearch(""); setSelectedApplicant(null);
        setShowApplicantDropdown(false); setValidationError(null);
        setSelectedVp(null); setConfirmOpen(false); setOverrideHierarchy(false);
    };

    const handleClose = () => { fullReset(); onClose(); };
    const handleSubmitClick = () => {
        if (!applicationType || !selectedApplicant || (!isVpRole && !selectedVp)) {
            setValidationError("Please fill in all required fields."); return;
        }
        setValidationError(null); setConfirmOpen(true);
    };

    const handleConfirm = () => {
        const initPayload = {
            applicationType: applicationType,
            bonusRecipientName: selectedApplicant!.EmployeeName,
            bonusRecipientEmail: selectedApplicant!.EmployeeEmail,
            submitterName: loggedInUserName,
            submitterEmail: loggedInUserEmail,
            submitterRole: selectedRole,
            vpName: selectedVp?.EmployeeName ?? "",
            vpEmail: selectedVp?.EmployeeEmail ?? "",
            applicantEmployeeNumber: selectedApplicant!.EmployeeNumber,
            hasFoundationCalls: selectedApplicant!.HasFoundationCalls ?? false,
            hasOverriddenHierarchy: overrideHierarchy,
            bonusRecipientDetails: selectedApplicant,
            year: parseInt(year), quarter: prevQ.quarter
        };
        const qs = `?mode=review&init=${encodeURIComponent(btoa(JSON.stringify(initPayload)))}`;
        handleClose();
        navigate(`${getRouteForApplicationType(applicationType)}/0${qs}`);
    };

    const isFormValid = !!applicationType && !!selectedApplicant && (isVpRole || !!selectedVp) && !hasNoEmployees && !hasNoVp;

    const summaryRows = [
        { label: `Submitter (${roleTypeLabel})`, value: loggedInUserName || "—" },
        { label: "Application Type", value: applicationType || "—" },
        { label: "Bonus Recipient", value: selectedApplicant?.EmployeeName ?? "—" },
        ...(!isVpRole ? [{ label: "VP", value: selectedVp?.EmployeeName ?? "—" }] : []),
        { label: "Period", value: `${year} · ${quarter}` },
        ...(overrideHierarchy ? [{ label: "Hierarchy", value: "⚠️ Override Active", isWarning: true }] : []),
    ];

    return (
        <>
            {/* ── Step 1: Form dialog ─────────────────────────────────────── */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: PAPER_SX }}>

                {/* Header */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "24px 32px", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 40, height: 40, background: "var(--muted)", border: "1px solid var(--border)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <DocumentTextIcon style={{ width: 20, height: 20, color: "var(--muted-foreground)" }} />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: "1.0625rem", fontWeight: 600, color: "var(--card-foreground)", letterSpacing: "-0.01em" }}>
                            Create Bonus Application
                        </h2>
                        <p style={{ margin: "3px 0 0", fontSize: "0.8125rem", color: "var(--muted-foreground)" }}>
                            Fill in bonus recipient details below
                        </p>
                    </div>
                </div>

                <DialogContent sx={{ padding: "0 !important", background: "var(--card)" }}>
                    <div style={{ padding: "20px 32px 28px", display: "flex", flexDirection: "column", gap: 20 }}>

                        {/* Submitter */}
                        <div>
                            <SectionLabel label="Submitter" />
                            <div style={{ marginTop: 12, background: "var(--muted)", borderRadius: "6px", padding: "11px 13px", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                    <Avatar name={loggedInUserName || "?"} role={roleVariant} size="lg" />
                                    <div style={{ minWidth: 0 }}>
                                        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--card-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{loggedInUserName || "—"}</p>
                                        <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--muted-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{loggedInUserEmail || "—"}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.07em", background: "var(--secondary)", color: "var(--secondary-foreground)", border: "1px solid var(--border)", padding: "3px 8px", borderRadius: "9999px", flexShrink: 0 }}>
                                    {roleTypeLabel}
                                </span>
                            </div>
                        </div>

                        {/* Warning banners */}
                        {hasNoEmployees && <WarningBanner title="No employees found for your role." body="Ensure you are authorized. If you need help, contact the QBA Admin Team." />}
                        {hasNoVp && <WarningBanner title="No VP found for your division." body="A VP assignment is required. Contact the QBA Admin Team." />}

                        {/* Override active banner */}
                        {overrideHierarchy && (
                            <div style={{ background: "#fff5f5", border: "1.5px solid #f87171", borderRadius: "8px", padding: "12px 16px", fontSize: 12, color: "#991b1b", display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ fontSize: 14 }}>⚠️</span>
                                    <span style={{ fontWeight: 700, fontSize: 13 }}>Override Active: Employee Hierarchy is Overridden</span>
                                </div>
                                <span style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
                                    All Construction &amp; Customer Care employees are available for selection — not just direct reports.
                                </span>
                            </div>
                        )}

                        {/* Application Details */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <SectionLabel label="Application Details" />

                            {/* Application Type */}
                            <Field label="Application Type" required>
                                {filteredApplicationTypeOptions.length === 1 ? (
                                    <ReadonlyInput value={filteredApplicationTypeOptions[0].label} />
                                ) : (
                                    <div style={{ position: "relative" }}>
                                        <select
                                            disabled={!overrideHierarchy && applicantPool.length === 1}
                                            value={applicationType}
                                            onChange={e => { setApplicationType(e.target.value); setValidationError(null); }}
                                            style={{ width: "100%", height: 36, padding: "0 32px 0 13px", border: "1px solid var(--input)", borderRadius: "8px", fontSize: 13, color: "var(--foreground)", background: "var(--background)", outline: "none", appearance: "none", fontFamily: "inherit" }}
                                        >
                                            <option value="" disabled>Select type…</option>
                                            {filteredApplicationTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                        <ChevronIcon />
                                    </div>
                                )}
                            </Field>

                            {/* Bonus Recipient */}
                            <Field
                                label="Bonus Recipient"
                                required
                                labelExtra={
                                    <OverrideToggle checked={overrideHierarchy} onChange={v => setOverrideHierarchy(v)} />
                                }
                            >
                                {!overrideHierarchy && applicantPool.length === 0 && applicationType ? (
                                    <p style={{ fontSize: 13, color: "var(--destructive)", margin: 0, padding: "6px 10px", background: "color-mix(in srgb, var(--destructive) 10%, transparent)", borderRadius: 6, border: "1px solid color-mix(in srgb, var(--destructive) 25%, transparent)" }}>
                                        No bonus recipients available for {applicationType} application type.
                                    </p>
                                ) : !overrideHierarchy && applicantPool.length === 1 ? (
                                    <SelectedEmployeeChip emp={applicantPool[0]} appType={applicationType} />
                                ) : (
                                    <div style={{ position: "relative" }} ref={applicantWrapRef}>
                                        <MagnifyingGlassIcon style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: showApplicantDropdown ? "var(--ring)" : "var(--muted-foreground)", pointerEvents: "none" }} />
                                        <input
                                            type="text"
                                            value={applicantSearch}
                                            onChange={e => { setApplicantSearch(e.target.value); setSelectedApplicant(null); if (applicationType || overrideHierarchy) setShowApplicantDropdown(true); setValidationError(null); }}
                                            onFocus={() => { if (applicationType || overrideHierarchy) setShowApplicantDropdown(true); }}
                                            placeholder={!applicationType && !overrideHierarchy ? "Select an application type first…" : isLoading ? "Loading employees…" : "Search by name, title, or employee #…"}
                                            disabled={(!applicationType && !overrideHierarchy) || isLoading}
                                            autoComplete="off"
                                            style={{ width: "100%", height: 36, padding: "0 32px 0 32px", border: "1px solid var(--input)", borderRadius: "8px", fontSize: 13, color: "var(--foreground)", background: (applicationType || overrideHierarchy) ? "var(--background)" : "var(--muted)", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                        />
                                        {isLoading && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}><CircularProgress size={14} /></div>}
                                        {showApplicantDropdown && !isLoading && (
                                            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--popover)", border: "1px solid var(--border)", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50, maxHeight: 176, overflowY: "auto" }}>
                                                {filteredApplicants.length > 0 ? filteredApplicants.map(emp => (
                                                    <div key={emp.EmployeeNumber} onMouseDown={() => handleSelectApplicant(emp)}
                                                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)" }}
                                                        onMouseOver={e => (e.currentTarget.style.background = "var(--accent)")}
                                                        onMouseOut={e => (e.currentTarget.style.background = "transparent")}
                                                    >
                                                        <Avatar name={emp.EmployeeName} role={getRecipientRoleVariant(applicationType)} />
                                                        <span style={{ fontSize: 13, color: "var(--popover-foreground)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.EmployeeName}</span>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                            <span style={{ fontSize: 11, color: "var(--muted-foreground)", fontFamily: "monospace" }}>{emp.JobTitle}</span>
                                                            {emp.ApplicationType && (
                                                                <>
                                                                    <span style={{ fontSize: 10, color: "var(--border)" }}>·</span>
                                                                    <span style={{ fontSize: 11, color: "var(--primary)", fontFamily: "monospace", fontWeight: 600 }}>{emp.ApplicationType}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )) : applicantSearch ? <p style={{ padding: "12px", fontSize: 13, color: "var(--muted-foreground)", margin: 0 }}>No employees found</p> : null}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Field>

                            {/* Year + Quarter — read-only */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <Field label="Year" required><ReadonlyInput value={year} /></Field>
                                <Field label="Quarter" required><ReadonlyInput value={quarter} /></Field>
                            </div>

                            {/* VP */}
                            {!isVpRole && (
                                <Field label="VP" required>
                                    {isLoading ? (
                                        <div style={{ height: 36, padding: "0 13px", border: "1px solid var(--input)", borderRadius: "8px", background: "var(--muted)", display: "flex", alignItems: "center", gap: 8 }}>
                                            <CircularProgress size={12} />
                                            <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>Loading VPs…</span>
                                        </div>
                                    ) : vpEmployees.length === 1 ? (
                                        <SelectedEmployeeChip emp={vpEmployees[0]} appType="vp" />
                                    ) : vpEmployees.length > 1 ? (
                                        <div style={{ position: "relative" }}>
                                            <select
                                                value={selectedVp?.EmployeeEmail ?? ""}
                                                onChange={e => { const vp = vpEmployees.find(v => v.EmployeeEmail === e.target.value) ?? null; setSelectedVp(vp); setValidationError(null); }}
                                                style={{ width: "100%", height: 36, padding: "0 32px 0 13px", border: "1px solid var(--input)", borderRadius: "8px", fontSize: 13, color: "var(--foreground)", background: "var(--background)", outline: "none", appearance: "none", fontFamily: "inherit" }}
                                            >
                                                <option value="" disabled>Select VP…</option>
                                                {vpEmployees.map(vp => <option key={vp.EmployeeEmail} value={vp.EmployeeEmail}>{vp.EmployeeName} — {vp.JobTitle}</option>)}
                                            </select>
                                            <ChevronIcon />
                                        </div>
                                    ) : (
                                        <ReadonlyInput value={defaultVpName || "VP not configured"} title={defaultVpEmail} warn={!defaultVpName} />
                                    )}
                                </Field>
                            )}
                        </div>

                        {/* Validation error */}
                        {validationError && (
                            <div style={{ background: "var(--destructive)", border: "1px solid var(--destructive)", borderRadius: "8px", padding: "12px 16px", fontSize: 13, color: "var(--primary-foreground)", opacity: 0.9 }}>
                                {validationError}
                            </div>
                        )}
                    </div>
                </DialogContent>

                {/* Footer */}
                <DialogActions sx={{ borderTop: "1px solid var(--border)", padding: "14px 32px", background: "var(--muted)", justifyContent: "space-between" }}>
                    <Button onClick={resetForm} sx={{ height: 36, px: 1.5, borderRadius: "8px", fontSize: "0.8125rem", fontWeight: 500, color: "var(--muted-foreground)", background: "transparent", textTransform: "none", minWidth: 0, "&:hover": { background: "var(--muted)", color: "var(--foreground)" } }}>
                        ↺ Reset
                    </Button>
                    <div style={{ display: "flex", gap: 8 }}>
                        <Button onClick={handleClose} sx={{ height: 36, px: 2.5, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, color: "var(--secondary-foreground)", border: "1px solid var(--border)", background: "var(--secondary)", textTransform: "none", "&:hover": { background: "var(--secondary)", opacity: 0.9, borderColor: "var(--border)" } }}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmitClick} disabled={!isFormValid} variant="contained" disableElevation
                            sx={{ height: 36, px: 2.5, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, textTransform: "none", background: "var(--primary)", color: "var(--primary-foreground)", boxShadow: "none", "&:hover": { background: "var(--primary)", opacity: 0.9, boxShadow: "none" }, "&:disabled": { background: "var(--muted)", color: "var(--muted-foreground)", boxShadow: "none" } }}>
                            Proceed
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>

            {/* ── Step 2: Confirm dialog ───────────────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: PAPER_SX }}>
                <div style={{ padding: "24px 24px 8px" }}>
                    <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "var(--card-foreground)", letterSpacing: "-0.01em" }}>Review &amp; Confirm</h3>
                    <p style={{ margin: "3px 0 0", fontSize: "0.8125rem", color: "var(--muted-foreground)" }}>Verify details before submitting</p>
                </div>
                <DialogContent sx={{ padding: "16px 24px !important", background: "var(--card)" }}>
                    <div style={{ borderRadius: "8px", border: "1px solid var(--border)", overflow: "hidden" }}>
                        {summaryRows.map((row, idx) => (
                            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "9px 14px", background: (row as any).isWarning ? "#fff5f5" : idx % 2 === 0 ? "var(--card)" : "var(--muted)", borderBottom: idx < summaryRows.length - 1 ? "1px solid var(--border)" : "none" }}>
                                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--muted-foreground)", whiteSpace: "nowrap", flexShrink: 0 }}>{row.label}</span>
                                <span style={{ fontSize: 13, fontWeight: 500, color: (row as any).isWarning ? "#991b1b" : row.value === "—" ? "var(--muted-foreground)" : "var(--card-foreground)", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.value}</span>
                            </div>
                        ))}
                    </div>
                </DialogContent>
                <DialogActions sx={{ borderTop: "1px solid var(--border)", padding: "14px 24px", background: "var(--muted)", gap: "8px", justifyContent: "flex-end" }}>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ height: 36, px: 2.5, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, color: "var(--foreground)", border: "1px solid var(--border)", background: "var(--background)", textTransform: "none", "&:hover": { background: "var(--accent)" } }}>
                        Go back
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" disableElevation sx={{ height: 36, px: 2.5, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, textTransform: "none", background: "var(--primary)", color: "var(--primary-foreground)", boxShadow: "none", "&:hover": { background: "var(--primary)", opacity: 0.9, boxShadow: "none" } }}>
                        Confirm &amp; Proceed
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BonusApplicationDialog;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>{label}</span>
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
);

const Field: React.FC<{ label: string; required?: boolean; labelExtra?: React.ReactNode; children: React.ReactNode }> = ({ label, required, labelExtra, children }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--foreground)" }}>
                {label}{required && <span style={{ color: "var(--destructive)", marginLeft: 2 }}>*</span>}
            </label>
            {labelExtra}
        </div>
        {children}
    </div>
);

const ReadonlyInput: React.FC<{ value: string; title?: string; warn?: boolean }> = ({ value, title, warn }) => (
    <input
        type="text" value={value} title={title} readOnly tabIndex={-1} aria-readonly="true"
        style={{ width: "100%", height: 36, padding: "0 13px", border: `1px solid ${warn ? "#fcd34d" : "var(--border)"}`, borderRadius: "8px", fontSize: 13, color: warn ? "#92400e" : "var(--muted-foreground)", background: warn ? "#fffbeb" : "var(--muted)", cursor: "not-allowed", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
    />
);

const ChevronIcon: React.FC = () => (
    <div style={{ pointerEvents: "none", position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "5px solid var(--muted-foreground)" }} />
);

const SelectedEmployeeChip: React.FC<{ emp: Employee; appType: string }> = ({ emp, appType }) => (
    <div style={{ height: 36, padding: "0 13px", border: "1px solid var(--border)", borderRadius: "8px", background: "var(--muted)", display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
        <Avatar name={emp.EmployeeName} role={getRecipientRoleVariant(appType)} />
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--foreground)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.EmployeeName}</span>
        <span style={{ fontSize: 11, color: "var(--muted-foreground)", fontFamily: "monospace", whiteSpace: "nowrap", flexShrink: 0 }}>{emp.JobTitle}</span>
    </div>
);

const WarningBanner: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px", padding: "14px 16px", fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
        <p style={{ margin: "0 0 4px", fontWeight: 600 }}>{title}</p>
        <p style={{ margin: 0 }}>{body}</p>
    </div>
);

const OverrideToggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", userSelect: "none" }}>
        <input
            type="checkbox"
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            style={{ width: 13, height: 13, accentColor: "var(--primary)", cursor: "pointer", flexShrink: 0 }}
        />
        <span style={{ fontSize: 11, fontWeight: 500, color: checked ? "var(--primary)" : "var(--muted-foreground)", whiteSpace: "nowrap" }}>
            Override Employee Hierarchy
        </span>
    </label>
);